import React, { useState, useEffect, useCallback } from 'react';
import monthlyReportService from '../service/monthlyReportService.mjs';
import { regionLabel } from '../page/PTAERegions.mjs';
import { localDayEndISO } from '../../utils/dateOnly.mjs';
import { statusBadge, fmtDate } from './DelegationModal.mjs';

/**
 * A regional or global director handing their monthly report approval to
 * someone while away (the user, 2026-10-22).
 *
 *   regional director - the regional stage of their own region's reports
 *   global director   - final approval, for the regions they tick
 *
 * Cover starts only when the delegate accepts, and lapses on the return date.
 * It adds a signer: the director can still approve meanwhile. The region's
 * admins are emailed at every step, and see the cover on the route when they
 * submit a report - they no longer choose it.
 */
const ROLE_WORDS = {
  admin: 'admin',
  regional_director: 'regional director',
  global_director: 'global director',
  finance_director: 'finance director',
};

const DirectorDelegationModal = ({ onClose }) => {
  const [candidates, setCandidates] = useState(null);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [userId, setUserId] = useState('');
  const [regions, setRegions] = useState([]);
  const [returnDate, setReturnDate] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, d] = await Promise.all([
        monthlyReportService.getDirectorDelegationCandidates(),
        monthlyReportService.getDirectorDelegations(),
      ]);
      setCandidates(c);
      setExisting(d);
      // Every region ticked to start with: a global director away is away from
      // all of them, and unticking is easier than ticking six.
      setRegions(prev => (prev.length ? prev : (c?.regions || [])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isFinal = candidates?.stage === 'final';
  const scope = isFinal ? regions : (candidates?.regions || []);
  const users = candidates?.users || [];
  const toggleRegion = (r) =>
    setRegions(prev => (prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]));

  const canSubmit = !!userId && !!returnDate && scope.length > 0 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    setError(null);
    try {
      const result = await monthlyReportService.createDirectorDelegation({
        delegateUserId: userId,
        regions: isFinal ? regions : undefined,
        // The END of the chosen day in this director's timezone, as the
        // auditor's delegation does, so the named day itself is covered.
        returnDate: localDayEndISO(returnDate),
        note: note.trim() || undefined,
      });
      setNotice(result.message || 'Delegation sent.');
      setUserId('');
      setReturnDate('');
      setNote('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    setError(null);
    setNotice(null);
    try {
      const result = await monthlyReportService.revokeDirectorDelegation(id);
      setNotice(result.message || 'Delegation revoked.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const mine = existing.filter(d => d.isMine);
  const toMe = existing.filter(d => !d.isMine);
  const stageWords = isFinal ? 'final approval' : 'regional director approval';

  return (
    <div className="fixed inset-0 z-[9200] bg-black/50 flex items-start justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-0 sm:my-8 flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-none">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Delegate approval</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {isFinal
                ? 'Let someone give final approval of monthly reports while you are away'
                : 'Let someone approve your region’s monthly reports while you are away'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mr-2 sm:mr-0 w-11 h-11 sm:w-auto sm:h-auto flex-shrink-0 flex items-center justify-center sm:block text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5 overflow-y-auto sm:overflow-visible">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg text-sm">{error}</div>
          )}
          {notice && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-lg text-sm">{notice}</div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label htmlFor="dd-delegate" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Delegate to <span className="text-stanford-red">*</span>
                  </label>
                  <select
                    id="dd-delegate"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                  >
                    <option value="">Select a person…</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} - {ROLE_WORDS[u.role] || String(u.role).replace(/_/g, ' ')}
                        {u.region ? ` (${regionLabel(u.region)})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {isFinal
                      ? 'Another global director, a finance director or a regional director.'
                      : 'Another regional director or an admin of your region, or a global or finance director.'}
                    {' '}They approve alongside you - you can still approve too.
                  </p>
                  {users.length === 0 && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
                      Nobody is set up who could cover for you.
                    </p>
                  )}
                </div>

                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {isFinal ? <>Regions it covers <span className="text-stanford-red">*</span></> : 'Region'}
                  </span>
                  {isFinal ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(candidates?.regions || []).map(r => (
                        <label
                          key={r}
                          className={`flex items-center gap-2 min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                            regions.includes(r)
                              ? 'bg-sky-50 border-sky-400 text-sky-900'
                              : 'bg-white border-gray-300 text-gray-700 hover:border-sky-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={regions.includes(r)}
                            onChange={() => toggleRegion(r)}
                            className="w-5 h-5 sm:w-auto sm:h-auto flex-shrink-0 accent-sky-600"
                          />
                          {regionLabel(r)}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-800">
                      {scope.length ? scope.map(regionLabel).join(', ') : 'Your profile has no region.'}
                    </p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dd-return" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Your return date <span className="text-stanford-red">*</span>
                    </label>
                    <input
                      id="dd-return"
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cover runs to the end of this day in your timezone, then lapses.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="dd-note" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Note (optional)
                    </label>
                    <input
                      id="dd-note"
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Included in the email"
                      className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                  They are asked to accept first. Your region&rsquo;s admins are told, and see the cover on the
                  approval route when they submit a report.
                </p>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-5 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {saving ? 'Sending…' : 'Send request'}
                  </button>
                </div>
              </form>

              {mine.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Delegations you have given</h4>
                  <ul className="space-y-2">
                    {mine.map(d => (
                      <li key={d.id} className="flex flex-wrap items-start justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2">
                        <div className="min-w-0 text-sm">
                          <p className="font-medium text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1 sm:block">
                            {d.delegateName}
                            {statusBadge(d) && (
                              <span className={`sm:ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${statusBadge(d).tone}`}>
                                {statusBadge(d).label}
                              </span>
                            )}
                          </p>
                          <p className="text-gray-600">
                            {d.stage === 'final' ? 'Final approval' : 'Regional approval'}
                            {' · '}{(d.regions || []).map(regionLabel).join(', ')}
                          </p>
                          {d.status === 'declined' && d.responseReason && (
                            <p className="text-xs text-red-700 mt-0.5 break-words">"{d.responseReason}"</p>
                          )}
                          {d.returnDate && (
                            <p className="text-xs text-gray-500">You return {fmtDate(d.returnDate)}</p>
                          )}
                        </div>
                        {(d.active || d.awaitingResponse) && (
                          <button
                            onClick={() => revoke(d.id)}
                            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-3 py-1 text-sm sm:text-xs border border-red-300 text-red-700 rounded-lg sm:rounded hover:bg-red-50"
                          >
                            {d.awaitingResponse ? 'Withdraw' : 'Revoke'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {toMe.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Delegated to you</h4>
                  <ul className="space-y-2 text-sm">
                    {toMe.map(d => (
                      <li key={d.id} className="border border-gray-200 rounded-lg px-3 py-2">
                        <p className="font-medium text-gray-900">
                          From {d.delegatedByName}
                          {statusBadge(d) && (
                            <span className={`ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${statusBadge(d).tone}`}>
                              {d.awaitingResponse ? 'awaiting your response' : statusBadge(d).label}
                            </span>
                          )}
                        </p>
                        <p className="text-gray-600">
                          {d.stage === 'final' ? 'Final approval' : 'Regional approval'}
                          {' · '}{(d.regions || []).map(regionLabel).join(', ')}
                          {d.accessEndsAt ? ` · until ${fmtDate(d.accessEndsAt)}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {mine.length === 0 && (
                <p className="text-xs text-gray-500">You have not delegated {stageWords} yet.</p>
              )}
            </>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectorDelegationModal;
